
import React, { useState, useMemo } from 'react';
import { MealSession, Language, AnalysisResult, SmartSuggestionItem, ChemicalCluster, SuspectStat } from '../types';
import { t } from '../locales';
import { AlertTriangle, Beaker, Stethoscope, ChevronDown, ChevronUp, ArrowRight, BrainCircuit, Activity, Lock, Star } from 'lucide-react';

interface HomeViewProps {
  sessions: MealSession[];
  onRecordMeal: () => void;
  onRecordSymptom: () => void;
  language: Language;
  analysisResult: AnalysisResult;
  onRunAnalysis: () => void;
  isAnalyzing: boolean;
  smartSuggestions: SmartSuggestionItem[];
  onLogSuggestion: (item: SmartSuggestionItem) => void;
  onOpenWater: () => void;
  onQuickAddWater: () => void;
}

const HomeView: React.FC<HomeViewProps> = ({ 
    sessions, 
    onRecordMeal, 
    onRecordSymptom, 
    language, 
    analysisResult, 
    onRunAnalysis,
    isAnalyzing,
    smartSuggestions,
    onLogSuggestion,
}) => {
  const [activeTab, setActiveTab] = useState<'trends' | 'chem' | 'doctor'>('trends');
  const [expandedSuspect, setExpandedSuspect] = useState<string | null>(null);

  // Calculate Streak (Visual only now, usually)
  const streak = useMemo(() => {
    const now = new Date();
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(now.getDate() - 10);
    const dailyCounts: Record<string, number> = {};
    sessions.forEach(session => {
        const date = new Date(session.timestamp);
        if (date >= tenDaysAgo && date <= now && session.items.length > 0) {
            dailyCounts[date.toDateString()] = (dailyCounts[date.toDateString()] || 0) + 1;
        }
    });
    let streakPoints = 0;
    Object.values(dailyCounts).forEach(count => { if (count >= 3) streakPoints++; });
    return Math.min(streakPoints, 10);
  }, [sessions]);
  
  // Calculate Total Unique Days for Analysis Unlock
  const uniqueDaysTracked = useMemo(() => {
      const dates = new Set(sessions.map(s => new Date(s.timestamp).toDateString()));
      return dates.size;
  }, [sessions]);

  const maxStreakDays = 10;
  const minDaysToUnlock = 10;
  const hasResults = analysisResult.isReady && analysisResult.topSuspects && analysisResult.topSuspects.length > 0;
  
  // Determine states based on total unique history, not just streak
  const isLocked = uniqueDaysTracked < minDaysToUnlock;
  // "Low Data" warning can be reused if they have unlocked but barely (e.g. exactly 10 days but very few meals)
  // For simplicity, if it's unlocked, we assume it's usable, but we can show a warning if < 15 days maybe?
  // Let's keep existing logic but mapped to uniqueDays
  const isLowData = uniqueDaysTracked >= minDaysToUnlock && uniqueDaysTracked < 14; 

  // Render Level 1: Statistical Trends
  const renderTrendsTab = () => (
      <div className="space-y-3 animate-slideIn">
          <div className="flex justify-between items-center mb-2">
              <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                  {t(language, 'trendsTitle')}
              </h4>
              <span className={`text-[9px] px-2 py-0.5 rounded-full ${isLowData ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                  {isLowData ? t(language, 'trendsSubtitleLowData') : t(language, 'trendsSubtitle')}
              </span>
          </div>
          
          {analysisResult.topSuspects.slice(0, 5).map((stat, idx) => (
              <div 
                key={idx} 
                onClick={() => setExpandedSuspect(expandedSuspect === stat.name ? null : stat.name)}
                className={`bg-white dark:bg-gray-800/50 rounded-xl p-3 border transition-all cursor-pointer ${expandedSuspect === stat.name ? 'border-emerald-500 ring-1 ring-emerald-500/20' : 'border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
              >
                  <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                          <span className="w-5 text-sm font-bold text-gray-300">#{idx + 1}</span>
                          <div>
                              <h5 className="font-bold text-gray-800 dark:text-gray-200 capitalize text-sm">{stat.name}</h5>
                              <p className="text-[10px] text-gray-400">{t(language, 'foundIn')} {stat.frequency} {t(language, 'mealsLower')} • {stat.incidentCount} {t(language, 'incidentsLower')}</p>
                          </div>
                      </div>
                      <div className="flex flex-col items-end">
                          <span className={`text-sm font-black ${stat.score > 2 ? 'text-red-500' : 'text-orange-500'}`}>
                              +{stat.score.toFixed(1)}
                          </span>
                          {expandedSuspect === stat.name ? <ChevronUp size={12} className="text-gray-400"/> : <ChevronDown size={12} className="text-gray-400"/>}
                      </div>
                  </div>
                  
                  {expandedSuspect === stat.name && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                          <div className="bg-orange-50 dark:bg-orange-900/10 p-2 rounded-lg mb-2">
                             <strong>{t(language, 'whySuspect')}</strong> {t(language, 'suspectExplanation')}
                          </div>
                          <p>{t(language, 'excludeAdvice')} <strong>{stat.name}</strong> {t(language, 'excludeAdviceSuffix')}</p>
                      </div>
                  )}
              </div>
          ))}
          <p className="text-[10px] text-center text-gray-400 italic mt-2">
              {t(language, 'scoreNote')}
          </p>
      </div>
  );

  // Render Level 2: Chemical Analysis
  const renderChemistryTab = () => (
      <div className="space-y-4 animate-slideIn">
          <div className="bg-blue-50 dark:bg-blue-900/10 p-3 rounded-xl border border-blue-100 dark:border-blue-800">
              <div className="flex items-start gap-3">
                  <Beaker className="text-blue-500 shrink-0 mt-0.5" size={16} />
                  <div>
                      <h4 className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase mb-1">{t(language, 'molecularMapping')}</h4>
                      <p className="text-[11px] text-blue-600 dark:text-blue-400 leading-snug">
                          {t(language, 'molecularDesc')}
                      </p>
                  </div>
              </div>
          </div>

          {(analysisResult.chemicalClusters || []).length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-xs">
                  {t(language, 'noClusters')}
              </div>
          ) : (
              (analysisResult.chemicalClusters || []).map((cluster, idx) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
                      <div className="flex justify-between items-start mb-2">
                          <h5 className="font-bold text-gray-800 dark:text-gray-200 text-sm">{cluster.chemicalName}</h5>
                          <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                              cluster.riskLevel === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                              {cluster.riskLevel} {t(language, 'risk')}
                          </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">
                          {cluster.description}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                          {cluster.foundInIngredients.map((ing, i) => (
                              <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-[10px] font-medium capitalize">
                                  {ing}
                              </span>
                          ))}
                      </div>
                  </div>
              ))
          )}
      </div>
  );

  // Render Level 3: Medical Insight
  const renderDoctorTab = () => (
      <div className="space-y-4 animate-slideIn">
          <div className="bg-purple-50 dark:bg-purple-900/10 p-4 rounded-2xl border border-purple-100 dark:border-purple-800 relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-2 -mr-2 w-16 h-16 bg-purple-200 dark:bg-purple-800 rounded-full blur-2xl opacity-50"></div>
              
              <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                      <div className="p-1.5 bg-purple-100 dark:bg-purple-800/50 rounded-lg text-purple-600 dark:text-purple-300">
                          <BrainCircuit size={18} />
                      </div>
                      <h4 className="text-sm font-bold text-purple-800 dark:text-purple-200">{t(language, 'geminiAnalysis')}</h4>
                  </div>
                  
                  {analysisResult.deepInsight ? (
                      <div className="space-y-4">
                          <div>
                              <h5 className="text-[10px] font-bold text-purple-400 uppercase tracking-widest mb-1">{t(language, 'clinicalSummary')}</h5>
                              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                                  {analysisResult.deepInsight.summary}
                              </p>
                          </div>
                          
                          <div className="grid grid-cols-1 gap-3">
                              <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-purple-100 dark:border-purple-900/50">
                                  <h5 className="text-[10px] font-bold text-gray-400 uppercase mb-1">{t(language, 'potentialProfile')}</h5>
                                  <p className="text-xs font-bold text-purple-600 dark:text-purple-400">{analysisResult.deepInsight.dietaryType}</p>
                              </div>
                              
                              <div className="bg-emerald-50 dark:bg-emerald-900/10 p-3 rounded-xl border border-emerald-100 dark:border-emerald-900/50">
                                  <h5 className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase mb-1 flex items-center gap-1">
                                      <Stethoscope size={10} /> {t(language, 'recommendation')}
                                  </h5>
                                  <p className="text-xs text-gray-700 dark:text-gray-300 italic">
                                      "{analysisResult.deepInsight.recommendation}"
                                  </p>
                              </div>
                          </div>
                      </div>
                  ) : (
                      <div className="text-center py-6 text-gray-400 text-xs">
                          {t(language, 'analysisPending')}
                      </div>
                  )}
              </div>
          </div>
          
          <div className="text-[10px] text-gray-400 text-center px-4">
              {isLowData ? (
                  <span className="text-amber-500 dark:text-amber-400 font-bold">{t(language, 'analysisDisclaimerLowData')}</span>
              ) : (
                  t(language, 'analysisDisclaimer')
              )}
          </div>
      </div>
  );

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 transition-colors relative">
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-48 no-scrollbar">
          {/* Hero Image */}
          <div className="relative h-32 shrink-0 w-full overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-500 animate-pulse bg-cover bg-center" style={{ animationDuration: '3s' }}></div>
            <img 
                src="https://images.unsplash.com/photo-1498837167922-ddd27525d352?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Healthy food on table" 
                className="absolute inset-0 w-full h-full object-cover opacity-90 dark:opacity-60 transition-opacity duration-700 ease-in-out"
                onLoad={(e) => e.currentTarget.classList.remove('opacity-0')}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-5">
                <div>
                    <p className="text-gray-200 text-xs font-medium mb-0.5">{t(language, 'welcome')}</p>
                    <h1 className="text-white text-2xl font-bold">{t(language, 'hello')}</h1>
                </div>
            </div>
          </div>

          <div className="px-5 mt-4 flex flex-col gap-6 relative z-10">
            {/* Streak Card */}
            <div id="tour-streak" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-100 dark:border-gray-700 shrink-0">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-0.5">{t(language, 'streakTitle')}</h3>
                        <p className="text-gray-400 dark:text-gray-500 text-[10px]">{t(language, 'streakSubtitle')}</p>
                    </div>
                    <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-500">{streak}</span>
                        <span className="text-xs text-gray-400 font-medium">/ 10</span>
                    </div>
                </div>
                
                {/* Visual Points (Dots) with Progress Bar */}
                <div className="relative flex justify-between items-center px-1">
                    {/* Background Track Line */}
                    <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-100 dark:bg-gray-700 -z-10 rounded-full mx-1"></div>
                    
                    {/* Active Progress Line */}
                    <div 
                        className="absolute top-1/2 left-0 h-1 bg-emerald-500 -z-10 rounded-full transition-all duration-700 ease-out mx-1" 
                        style={{ width: `${(Math.max(0, streak - 1) / (maxStreakDays - 1)) * 100}%` }}
                    ></div>

                    {/* Dots */}
                    {[...Array(maxStreakDays)].map((_, i) => (
                        <div 
                            key={i} 
                            className={`w-2.5 h-2.5 rounded-full transition-all duration-500 z-10 border border-white dark:border-gray-800 ${
                                i < streak 
                                ? 'bg-emerald-500 shadow-sm scale-110' 
                                : 'bg-gray-200 dark:bg-gray-700 scale-90'
                            }`}
                        />
                    ))}
                </div>
            </div>

            {/* AI Insights - MAIN FEATURE */}
            <div id="tour-analysis" className="flex flex-col gap-4">
               <div className="flex items-center justify-between">
                   <h3 className="font-bold text-gray-800 dark:text-white text-lg flex items-center gap-2">
                       {isLocked ? <Lock size={20} className="text-gray-400" /> : <BrainCircuit className={isLowData && hasResults ? "text-amber-500" : "text-emerald-500"} />}
                       {isLocked ? t(language, 'aiInsightsLocked') : (isLowData && hasResults ? t(language, 'aiInsightsPreliminary') : t(language, 'aiInsights'))}
                   </h3>
                   {!isAnalyzing && (
                        <button 
                           onClick={isLocked ? undefined : onRunAnalysis} 
                           disabled={isLocked}
                           className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
                               isLocked 
                               ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed' 
                               : 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100'
                           }`}
                        >
                           {isLocked ? t(language, 'locked') : (hasResults ? t(language, 'generateAnalysis') : t(language, 'startAnalysis'))}
                        </button>
                   )}
               </div>

               {isAnalyzing ? (
                   <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center border border-emerald-100 dark:border-emerald-900/50">
                       <div className="relative">
                           <div className="w-12 h-12 border-4 border-emerald-100 border-t-emerald-500 rounded-full animate-spin"></div>
                           <div className="absolute inset-0 flex items-center justify-center">
                               <BrainCircuit size={16} className="text-emerald-500" />
                           </div>
                       </div>
                       <p className="mt-4 text-sm font-medium text-gray-600 dark:text-gray-300">{t(language, 'processing')}</p>
                       <p className="text-xs text-gray-400 mt-1">Connecting to Gemini 3.0</p>
                   </div>
               ) : (isLocked) ? (
                   /* Locked State */
                   <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl p-6 text-center border border-gray-200 dark:border-gray-700 relative overflow-hidden">
                       <div className="absolute inset-0 opacity-5 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,#000_10px,#000_20px)]"></div>
                       <div className="relative z-10 flex flex-col items-center gap-3">
                           <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-400">
                               <Lock size={24} />
                           </div>
                           <h3 className="font-bold text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wide">{t(language, 'aiInsightsLocked')}</h3>
                           <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs leading-relaxed">
                               {language === 'fr' ? 'Accumulez 10 jours d\'historique pour débloquer l\'analyse.' : 'Collect 10 days of history to unlock analysis.'}
                           </p>
                           <div className="w-full max-w-[150px] h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full mt-2 overflow-hidden">
                               <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${(uniqueDaysTracked / minDaysToUnlock) * 100}%` }}></div>
                           </div>
                           <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                               {uniqueDaysTracked} / {minDaysToUnlock} Days
                           </p>
                       </div>
                   </div>
               ) : (hasResults) ? (
                   <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg border overflow-hidden min-h-[400px] ${isLowData ? 'border-amber-200 dark:border-amber-900/50' : 'border-gray-100 dark:border-gray-700'}`}>
                       {/* Low Data Warning Banner */}
                       {isLowData && (
                           <div className="bg-amber-50 dark:bg-amber-900/30 px-4 py-2 flex items-center justify-center gap-2 border-b border-amber-100 dark:border-amber-800/50">
                               <AlertTriangle size={12} className="text-amber-500" />
                               <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                                   {t(language, 'lowDataWarning')} - {15 - uniqueDaysTracked} days left for full accuracy
                               </span>
                           </div>
                       )}

                       {/* Tabs Header */}
                       <div className="flex border-b border-gray-100 dark:border-gray-700">
                           <button 
                                onClick={() => setActiveTab('trends')}
                                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide transition-colors ${activeTab === 'trends' ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50 dark:bg-emerald-900/10' : 'text-gray-400 hover:text-gray-600'}`}
                           >
                               L1: Trends
                           </button>
                           <button 
                                onClick={() => setActiveTab('chem')}
                                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide transition-colors ${activeTab === 'chem' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50 dark:bg-blue-900/10' : 'text-gray-400 hover:text-gray-600'}`}
                           >
                               L2: Chem
                           </button>
                           <button 
                                onClick={() => setActiveTab('doctor')}
                                className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide transition-colors ${activeTab === 'doctor' ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50 dark:bg-purple-900/10' : 'text-gray-400 hover:text-gray-600'}`}
                           >
                               L3: Insight
                           </button>
                       </div>

                       {/* Tab Content */}
                       <div className="p-5 bg-gray-50/30 dark:bg-gray-900/30 min-h-[300px]">
                           {activeTab === 'trends' && renderTrendsTab()}
                           {activeTab === 'chem' && renderChemistryTab()}
                           {activeTab === 'doctor' && renderDoctorTab()}
                       </div>
                   </div>
               ) : (
                   /* Empty State */
                   <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl p-6 text-white text-center shadow-lg relative overflow-hidden">
                       <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                       <div className="relative z-10">
                           <Activity size={32} className="mx-auto mb-3 opacity-80" />
                           <h3 className="font-bold text-lg mb-2">{isLowData ? t(language, 'readyToAnalyzeLowData') : t(language, 'readyToAnalyze')}</h3>
                           <p className="text-xs text-emerald-50 opacity-90 mb-4 leading-relaxed max-w-xs mx-auto">
                               {isLowData ? t(language, 'notEnoughDataLowData') : t(language, 'notEnoughData')}
                           </p>
                           <button 
                                onClick={onRunAnalysis}
                                className="bg-white text-emerald-800 px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg hover:scale-105 transition-transform active:scale-95"
                           >
                               {t(language, 'startAnalysis')}
                           </button>
                           <p className="mt-3 text-[10px] opacity-60">
                               {language === 'fr' ? 'Données historiques suffisantes.' : 'Historical data sufficient.'}
                           </p>
                       </div>
                   </div>
               )}
            </div>
          </div>
      </div>

      {/* Fixed Bottom Action Container */}
      <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-3 z-20 flex flex-col justify-end">
          {smartSuggestions.length > 0 && (
            <div id="home-smart-suggestions" className="w-full">
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    {t(language, 'smartSuggestions')}
                </h4>
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                    {smartSuggestions.map((suggestion, idx) => (
                        <button 
                            key={idx}
                            onClick={() => onLogSuggestion(suggestion)}
                            className={`px-3 py-1.5 border rounded-full text-xs font-medium transition-colors shadow-sm flex-shrink-0 flex items-center gap-1.5 ${
                                suggestion.type === 'favorite' 
                                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100'
                                : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:border-emerald-200'
                            }`}
                        >
                            {suggestion.type === 'favorite' && <Star size={10} className="fill-current" />}
                            {suggestion.label}
                        </button>
                    ))}
                </div>
            </div>
          )}

          <div className="flex items-center gap-3">
              <button id="home-record-meal-btn" onClick={onRecordMeal} className="flex-1 flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white py-2 px-2 rounded-full shadow-lg transition-transform active:scale-95 group min-w-0 h-auto min-h-[3rem]">
                  <span className="font-bold text-xs uppercase tracking-wide">{t(language, 'recordMeal')}</span>
              </button>
              <button id="home-record-symptom-btn" onClick={onRecordSymptom} className="flex-1 flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-400 text-white py-2 px-2 rounded-full shadow-lg transition-transform active:scale-95 group min-w-0 h-auto min-h-[3rem]">
                  <span className="font-bold text-xs uppercase tracking-wide">{t(language, 'recordSymptom')}</span>
              </button>
          </div>
      </div>
    </div>
  );
};

export default HomeView;
